// Refactored Database Migration Adapter - Coordinates all migration concerns

import { MigrationResponse, MigrationOperation, EntityType, BatchResult } from './interfaces';
import { MigrationClient } from './MigrationClient';
import { PatientHandler } from './handlers/PatientHandler';
import { RecordingHandler } from './handlers/RecordingHandler';
import { UserHandler } from './handlers/UserHandler';
import { TemplateHandler } from './handlers/TemplateHandler';
import { createTransformer } from './transformers';

export class DatabaseMigrationAdapter {
  private static instance: DatabaseMigrationAdapter;
  private migrationClient = MigrationClient.getInstance();
  private patientHandler = new PatientHandler();
  private recordingHandler = new RecordingHandler();
  private userHandler = new UserHandler();
  private templateHandler = new TemplateHandler();
  private excludedEntities = ['Details', 'DetailsType', 'BillingCodes', 'TemplateSubsection', 'TemplateField'];

  private constructor() {
    console.log('[DATABASE-MIGRATION-ADAPTER] Refactored adapter initialized with separated concerns');
  }

  public static getInstance(): DatabaseMigrationAdapter {
    if (!DatabaseMigrationAdapter.instance) {
      DatabaseMigrationAdapter.instance = new DatabaseMigrationAdapter();
    }
    return DatabaseMigrationAdapter.instance;
  }

  // Generic operation handler that routes to appropriate handlers or uses direct transformation
  private async handleGenericOperation(
    operation: MigrationOperation,
    entityType: EntityType,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    console.log(`[MIGRATION-ADAPTER] ${operation} ${entityType} operation started`);
    
    // Check if entity should be skipped
    if (this.excludedEntities.includes(entityType)) {
      console.log(`[MIGRATION-ADAPTER] SKIPPED: ${entityType} not synced per migration rules`);
      return {
        success: true,
        operation,
        entity_type: entityType,
        skipped: true,
        reason: 'Entity type not synced per migration rules',
        timestamp: new Date().toISOString()
      };
    }

    try {
      // Extract the actual data from Amplify result or use input data
      const entityData = (amplifyResult as Record<string, Record<string, unknown>>)?.data?.[`create${entityType}`] || amplifyData;

      // Transform the data using appropriate transformer
      const transformer = createTransformer(entityType);
      const transformedData = await transformer.transform(entityData as Record<string, unknown>);

      // Check if transformation decided to skip
      if ((transformedData as Record<string, unknown>).skipped) {
        return {
          success: true,
          operation,
          entity_type: entityType,
          skipped: true,
          reason: (transformedData as Record<string, unknown>).reason as string,
          timestamp: new Date().toISOString()
        };
      }

      // Send to migration service
      return await this.migrationClient.invoke(operation, entityType, transformedData);
    } catch (error) {
      console.error(`[MIGRATION-ADAPTER] ${operation} ${entityType} failed:`, error);
      return {
        success: false,
        operation,
        entity_type: entityType,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      };
    }
  }

  // Patient operations
  public async handlePatientOperation(
    operation: MigrationOperation,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    return await this.patientHandler.handleOperation(operation, amplifyData, amplifyResult);
  }

  // Case operations
  public async handleCaseOperation(
    operation: MigrationOperation,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    return await this.handleGenericOperation(operation, 'Case', amplifyData, amplifyResult);
  }

    // Template operations (with complex nested structure handling)
  public async handleTemplateOperation(
    operation: MigrationOperation,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    return await this.templateHandler.handleOperation(operation, amplifyData, amplifyResult);
  }

  // Recording operations  
  public async handleRecordingOperation(
    operation: MigrationOperation,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    return await this.recordingHandler.handleOperation(operation, amplifyData, amplifyResult);
  }

  // User operations
  public async handleUserOperation(
    operation: MigrationOperation,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    return await this.userHandler.handleOperation(operation, amplifyData, amplifyResult);
  }

  // Template section operations
  public async handleTemplateSectionOperation(
    operation: MigrationOperation,
    sectionData: { template_id: string; name: string; content?: string; order_index?: number },
  ): Promise<MigrationResponse> {
    const payload = {
      template_id: sectionData.template_id,
      name: sectionData.name,
      content: sectionData.content ?? '',
      order_index: typeof sectionData.order_index === 'number' ? sectionData.order_index : 0,
      created_by: null, // Will be auto-populated by MigrationClient
      owners: [], // Will be auto-populated by MigrationClient
    };
    return await this.migrationClient.invoke(operation, 'TemplateSection', payload);
  }

  // Section operations
  public async handleSectionOperation(
    operation: MigrationOperation,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    return await this.handleGenericOperation(operation, 'Section', amplifyData, amplifyResult);
  }

  // Textbox operations
  public async handleTextboxOperation(
    operation: MigrationOperation,
    amplifyData: Record<string, unknown>,
    amplifyResult?: Record<string, unknown>
  ): Promise<MigrationResponse> {
    return await this.handleGenericOperation(operation, 'Textbox', amplifyData, amplifyResult);
  }

  // Recording view operations - delegate to RecordingHandler
  public async upsertRecordingOnView(recordingId: string, amplifyRecordingData?: Record<string, unknown>): Promise<MigrationResponse> {
    return await this.recordingHandler.upsertRecordingOnView(recordingId, amplifyRecordingData);
  }

  public async batchUpsertRecordingsOnView(recordings: Array<{ id: string; data?: Record<string, unknown> }>): Promise<BatchResult> {
    return await this.recordingHandler.batchUpsertRecordingsOnView(recordings);
  }

  // Template nested structure creation (kept here due to complexity)
  private async createTemplateSections(templateData: TemplateWithSections): Promise<void> {
    if (!templateData.sections?.items) return;

    console.log(`[MIGRATION-ADAPTER] Creating ${templateData.sections.items.length} sections for template ${templateData.id}`);

    for (const section of templateData.sections.items) {
      try {
        // Collect textbox values for section content (Textbox.value -> Section.content)
        let sectionContent = '';
        if (section.textboxes?.items) {
          const textboxValues = section.textboxes.items
            .filter((textbox: TextboxItem) => textbox?.value) // Only include textboxes with values
            .map((textbox: TextboxItem) => textbox.value)
            .join('\n\n');
          sectionContent = textboxValues;
          console.log(`[MIGRATION-ADAPTER] Collected ${section.textboxes.items.length} textbox values for section ${section.name}`);
        }

        // Create template section with textbox values as content
        const sectionPayload = {
          id: section.id,
          name: section.name,
          template_id: templateData.id,
          content: sectionContent, // Textbox.value -> Section.content
          order_index: section.order || 0,
          created_at: section.createdAt,
          updated_at: section.updatedAt,
        };

        await this.migrationClient.invoke('CREATE', 'TemplateSection', sectionPayload);
        console.log(`[MIGRATION-ADAPTER] Section created with content: ${section.name} (${sectionContent.length} chars)`);

        // Create subsections if they exist
        if (section.subsections?.items) {
          await this.createTemplateSubsections(section.subsections.items, section.id, templateData.id);
        }

        // Skip creating individual textboxes since their values are now in section content
      } catch (error) {
        console.error(`[MIGRATION-ADAPTER] Failed to create section ${section.name}:`, error);
        throw error;
      }
    }
  }

  private async createTemplateSubsections(subsections: SubsectionItem[], sectionId: string, templateId: string): Promise<void> {
    console.log(`[MIGRATION-ADAPTER] Creating ${subsections.length} subsections for section ${sectionId}`);

    for (const subsection of subsections) {
      try {
        const subsectionPayload = {
          id: subsection.id,
          name: subsection.name,
          section_id: sectionId,
          template_id: templateId,
          field_order: subsection.fieldOrder || [],
          created_at: subsection.createdAt,
          updated_at: subsection.updatedAt,
          owners: subsection.owners || [],
          version: subsection._version || 1
        };

        await this.migrationClient.invoke('CREATE', 'TemplateSubsection', subsectionPayload);
        console.log(`[MIGRATION-ADAPTER] Subsection created: ${subsection.name}`);

        // Create fields if they exist
        if (subsection.fields?.items) {
          await this.createTemplateFields(subsection.fields.items, subsection.id, sectionId, templateId);
        }
      } catch (error) {
        console.error(`[MIGRATION-ADAPTER] Failed to create subsection ${subsection.name}:`, error);
        throw error;
      }
    }
  }

  private async createTemplateFields(fields: FieldItem[], subsectionId: string, sectionId: string, templateId: string): Promise<void> {
    console.log(`[MIGRATION-ADAPTER] Creating ${fields.length} fields for subsection ${subsectionId}`);

    for (const field of fields) {
      try {
        const fieldPayload = {
          id: field.id,
          name: field.name,
          field_type: field.fieldType || 'text',
          subsection_id: subsectionId,
          section_id: sectionId,
          template_id: templateId,
          placeholder: field.placeholder || '',
          is_required: field.isRequired || false,
          options: field.options || [],
          created_at: field.createdAt,
          updated_at: field.updatedAt,
          owners: field.owners || [],
          version: field._version || 1
        };

        await this.migrationClient.invoke('CREATE', 'TemplateField', fieldPayload);
        console.log(`[MIGRATION-ADAPTER] Field created: ${field.name}`);
      } catch (error) {
        console.error(`[MIGRATION-ADAPTER] Failed to create field ${field.name}:`, error);
        throw error;
      }
    }
  }
}

// Type definitions for template nested structure
interface TextboxItem {
  id: string;
  value: string;
  name?: string;
}

interface FieldItem {
  id: string;
  name: string;
  fieldType?: string;
  placeholder?: string;
  isRequired?: boolean;
  options?: string[];
  createdAt?: string;
  updatedAt?: string;
  owners?: string[];
  _version?: number;
}

interface SubsectionItem {
  id: string;
  name: string;
  fieldOrder?: string[];
  createdAt?: string;
  updatedAt?: string;
  owners?: string[];
  _version?: number;
  fields?: {
    items: FieldItem[];
  };
}

interface SectionItem {
  id: string;
  name: string;
  order?: number;
  createdAt?: string;
  updatedAt?: string;
  textboxes?: {
    items: TextboxItem[];
  };
  subsections?: {
    items: SubsectionItem[];
  };
}

interface TemplateWithSections {
  id: string;
  sections: {
    items: SectionItem[];
  };
} 